import dayjs from "dayjs";
import { useMemo } from "react";
import { CircularProgress, Stack } from "@mui/material";
import { PrefTabs, usePagePrefs } from "@pepsico/pref-permissions";

import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import {
  OrthoApprovalStatus,
  TaskStatus,
  type GrowerGeoResponse,
  type PlotGeoResponse,
  type SiteGeoResponse,
  type TrialGeoResponse,
} from "api/model";
import { useGetPlotsGeoByLocation } from "api/plots-geo/plots-geo";
import { useGetSitesGeoByLocation } from "api/sites-geo/sites-geo";
import { useGetTrialsGeoByLocation } from "api/trials-geo/trials-geo";
import { readSelectedTrialGeoId } from "components/PlotMap/utils/selectionPrefs";
import { useCogOrtho, type LocationOrtho } from "context/CogOrtho";
import {
  getAnalyzeCanopyForTrial,
  getAnalyzeEmergenceForTrial,
  getOrthoWorkflowKey,
  getWorkflowFolderNames,
  useOrthoWorkflows,
} from "context/OrthoWorkflow";
import { usePlotMap } from "context/PlotMap";
import {
  getTrialGeoLabel,
  hasPlotsGeo,
} from "context/PlotMap/plotGridGeoUtils";
import { ANALYSIS_TAB_PREF_KEY } from "defs";

import AnalysisEnclosingOrthos from "./AnalysisEnclosingOrthos";
import AnalysisResultsTab from "./AnalysisResultsTab";
import AnalysisSelectionSummary from "./AnalysisSelectionSummary";
import GoToPlotsPrompt from "./GoToPlotsPrompt";
import PlotTrialSection from "./PlotTrialSection";

const getGrowerLabel = (
  grower: GrowerGeoResponse | null,
  isPending: boolean,
): string => {
  if (grower != null) {
    return grower.grower_label?.trim() || "Unlabelled Grower Boundary";
  }

  return isPending ? "Loading…" : "—";
};

const getSiteLabel = (
  site: SiteGeoResponse | null,
  isPending: boolean,
): string => {
  if (site != null) {
    return site.site_label?.trim() || "Unlabelled Site Boundary";
  }

  return isPending ? "Loading…" : "—";
};

interface AnalysisPanelProps {}

const AnalysisPanel: React.FC<AnalysisPanelProps> = () => {
  const [prefs] = usePagePrefs();
  const {
    plot: {
      growerGeoId: selectedGrowerId,
      isDirty: isPlotGridDirty,
      isPlotGridMode,
      siteGeoId: selectedSiteId,
    },
  } = usePlotMap();
  const { orthos } = useCogOrtho();
  const { byKey } = useOrthoWorkflows();
  const locationUuid = (prefs.location as string | null | undefined) ?? "";

  const { data: trials = [] } = useGetTrialsGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): TrialGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );

  const selectedTrialId = readSelectedTrialGeoId(
    prefs,
    trials.map((trial) => trial.id),
  );

  const analyzedOrthos = useMemo((): LocationOrtho[] => {
    if (selectedTrialId == null) {
      return [];
    }
    const folderLabel = (ortho: LocationOrtho): string => {
      const workflow = byKey.get(getOrthoWorkflowKey(ortho));
      return workflow == null
        ? ""
        : getWorkflowFolderNames(workflow).join(", ");
    };
    const flightTimestamp = (ortho: LocationOrtho): number => {
      const parsed = dayjs(ortho.flight_date);
      return parsed.isValid() ? parsed.valueOf() : 0;
    };
    return orthos
      .filter((ortho) => {
        if (ortho.status !== OrthoApprovalStatus.approved) {
          return false;
        }
        const workflow = byKey.get(getOrthoWorkflowKey(ortho));
        return (
          getAnalyzeCanopyForTrial(workflow, selectedTrialId).status ===
            TaskStatus.completed ||
          getAnalyzeEmergenceForTrial(workflow, selectedTrialId).status ===
            TaskStatus.completed
        );
      })
      .toSorted(
        (a, b) =>
          flightTimestamp(a) - flightTimestamp(b) ||
          folderLabel(a).localeCompare(folderLabel(b)) ||
          a.id - b.id,
      );
  }, [byKey, orthos, selectedTrialId]);

  const { data: growers = [], isPending: isGrowersPending } =
    useGetGrowersGeoByLocation(
      { location_uuid: locationUuid },
      {
        query: {
          enabled: locationUuid !== "" && selectedTrialId != null,
          select: (res): GrowerGeoResponse[] =>
            res.status === 200 ? res.data : [],
        },
      },
    );

  const { data: sites = [], isPending: isSitesPending } =
    useGetSitesGeoByLocation(
      { location_uuid: locationUuid },
      {
        query: {
          enabled: locationUuid !== "",
          select: (res): SiteGeoResponse[] =>
            res.status === 200 ? res.data : [],
        },
      },
    );

  const { data: plotsGeo = [], isPending: isPlotsPending } =
    useGetPlotsGeoByLocation(
      {
        location_uuid: locationUuid,
        trial_id: selectedTrialId ?? undefined,
      },
      {
        query: {
          enabled: locationUuid !== "" && selectedTrialId != null,
          select: (res): PlotGeoResponse[] =>
            res.status === 200 ? res.data : [],
        },
      },
    );

  const selectedGrower =
    growers.find((grower) => grower.id === selectedGrowerId) ?? null;
  const selectedSite = sites.find((site) => site.id === selectedSiteId) ?? null;
  const selectedTrial =
    trials.find((trial) => trial.id === selectedTrialId) ?? null;
  const hasPlotGeometry = hasPlotsGeo(plotsGeo);
  const showAnalysis = !isPlotsPending && hasPlotGeometry;
  const hasAnalysisResults = analyzedOrthos.length > 0;
  const showNoPlotGeometry = !isPlotsPending && !hasPlotGeometry;

  return (
    <PlotTrialSection
      isDisabled={isPlotGridDirty || isPlotGridMode}
      sites={sites}
      title="Analysis"
      trials={trials}
    >
      {selectedTrialId != null && (
        <Stack spacing={2}>
          <AnalysisSelectionSummary
            growerLabel={getGrowerLabel(selectedGrower, isGrowersPending)}
            siteLabel={getSiteLabel(selectedSite, isSitesPending)}
            trialLabel={getTrialGeoLabel(selectedTrial, selectedTrialId)}
          />
          {isPlotsPending && (
            <CircularProgress aria-label="Loading plot geometry" size={24} />
          )}
          {showNoPlotGeometry && (
            <GoToPlotsPrompt
              header="No Plot Geometry"
              message="This trial has no plot geometry. Draw plots on the Plots tab before running analysis."
            />
          )}
          {showAnalysis && (
            <Stack spacing={2}>
              <PrefTabs
                param={ANALYSIS_TAB_PREF_KEY}
                tabs={{
                  results: {
                    component: AnalysisResultsTab,
                    componentProps: {
                      analyzedOrthos,
                      plotsGeo,
                      siteLabel: selectedSite?.site_label?.trim() ?? "site",
                      trialId: selectedTrialId,
                    },
                    disabled: !hasAnalysisResults,
                    label: "Results",
                  },
                  runs: {
                    component: AnalysisEnclosingOrthos,
                    componentProps: {
                      plotsGeo,
                      trialId: selectedTrialId,
                    },
                    label: "Runs",
                  },
                }}
              />
            </Stack>
          )}
        </Stack>
      )}
    </PlotTrialSection>
  );
};

export default AnalysisPanel;
